import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import HomeMileageEarningNoticeKr from '@di-shared/features/home/kr/benefit/HomeMileageEarningNotice.kr';
import HomeMileageInnerContentsKr, {
  HomeMileageInnerContentsOdometerError,
} from '@di-shared/features/home/kr/benefit/HomeMileageInnerContents.kr';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HomeMileageKrProps {
  vin: string;

  onClick(): void;
}

function HomeMileageKr({vin, onClick}: HomeMileageKrProps) {
  const {t} = useInferableTranslation();
  const {odometer} = useOdometer(vin);
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink), vin],
    queryFn: () => diApi.ecoMileageController.getInfoLink({vin}),
  });

  if (data.data.rtn_data.drvng_stt_cd === '000') {
    return <HomeMileageChallengeEndKr onClick={onClick} />;
  }

  return (
    <>
      <section className="section section_eco_mileage">
        <div className="section_container">
          <div className="section_container_header">
            <div className="container_header_title">
              <h3 className="label_medium_b">{t('T10122')}</h3>
              <strong
                className="title_xxsmall"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T10123')}
              </strong>
            </div>
            <div className="container_header_imgbox">
              <ImageSelector
                alt="eco_mileage_img"
                kiaLightSrc={() => import('@di-shared/assets/img/home/eco_mileage_car.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/home/eco_mileage_car.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/eco_mileage_car.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/eco_mileage_car.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/eco_mileage_car.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/eco_mileage_car.png')}
              />
            </div>
          </div>
          {odometer ? <HomeMileageInnerContentsKr vin={vin} /> : <HomeMileageInnerContentsOdometerError />}
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={() => {
              emitEventLog({
                event: 'mileage_management',
                interaction_type: 'check mileage',
                click_text: t('T10131'),
                click_location: 'body',
              });
              onClick();
            }}
          >
            {t('T10131')}
          </button>
        </div>
      </section>
      <ErrorBoundary fallback={<></>}>
        <Suspense fallback={<></>}>
          <HomeMileageEarningNoticeKr vin={vin} />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default HomeMileageKr;

export function HomeMileageLoading() {
  const {t} = useInferableTranslation();
  return (
    <section className="section section_eco_mileage">
      <div className="section_container">
        <div className="section_container_header skeleton">
          <div className="container_header_title">
            <h3 className="label_medium_b">{t('T10122')}</h3>
            <strong
              className="title_xxsmall"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T10123')}
            </strong>
          </div>
          <div className="container_header_imgbox">
            <ImageSelector
              alt="eco_mileage_img"
              kiaLightSrc={() => import('@di-shared/assets/img/home/eco_mileage_car.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/home/eco_mileage_car.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/eco_mileage_car.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/eco_mileage_car.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/eco_mileage_car.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/eco_mileage_car.png')}
            />
          </div>
        </div>
        <ul className="eco_state_list">
          <li className="eco_state_item skeleton">
            <strong className="eco_state_title">{t('T10124')}</strong>
            <p className="eco_state_desc">{t('T10125_$mileage', {mileage: 0})}</p>
            <div className="badge_box">
              <span className="badge badge_basic_container badge_20px">{t('T10126')}</span>
            </div>
          </li>
          <li className="eco_state_item skeleton">
            <strong className="eco_state_title">{t('T10127')}</strong>
            <p className="eco_state_desc">{t('T10125_$mileage', {mileage: 0})}</p>
            <div className="badge_box">
              <span className="badge badge_container_red badge_20px">{t('T10126')}</span>
            </div>
          </li>
        </ul>
        <button
          type="button"
          className="btn btn_large btn_full disabled skeleton"
          disabled
        >
          {t('T10131')}
        </button>
      </div>
    </section>
  );
}

const TEL_CUSTOMER_CENTER = '02-120';

function HomeMileageChallengeEndKr({onClick}: {onClick: () => void}) {
  const {t} = useInferableTranslation();
  return (
    <section className="section section_eco_mileage">
      <div className="section_container section_container_complete">
        <div className="section_container_header">
          <div className="container_header_title">
            <h3 className="label_medium_b">{t('T10122')}</h3>
            <strong className="title_xxsmall">{t('T30622')}</strong>
          </div>
          <div className="container_header_imgbox">
            <ImageSelector
              alt="eco_mileage_coin_img"
              kiaLightSrc={() => import('@di-shared/assets/img/home/eco_mileage_coin.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/home/eco_mileage_coin.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/eco_mileage_coin.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/eco_mileage_coin.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/eco_mileage_coin.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/eco_mileage_coin.png')}
            />
          </div>
        </div>
        <p className="section_container_desc">{t('T30623')}</p>
        <div className="section_container_box">
          <strong className="notice_title">{t('T30624')}</strong>
          <ul className="notice_list">
            <li className="notice_item">{t('T40082')}</li>
            <li className="notice_item">{t('T30625')}</li>
            <li className="notice_item">{t('T30626')}</li>
          </ul>
        </div>

        <p className="notice_desc">{t('T30627_$tel', {tel: TEL_CUSTOMER_CENTER})}</p>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onClick}
        >
          {t('T10131')}
        </button>
      </div>
    </section>
  );
}
